import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import nds from '@nerdwallet/base-styles/classes';
import { Box, Title, Text, Link } from '@nerdwallet/currency';

import {
  stringInputSpec,
  stringTextAreaSpec,
} from '../../../../lib/editing-configs';
import parseStyleFormatting from '../parse-awards-2023-formatting';
import styles from './layout-l2-insurance.module.less';
import { useScrollAnimation } from '../useScrollAnimation';

const LayoutL2Insurance = ({
  title,
  subtitle,
  brandLogoUrl,
  brandLogoLink,
  desktopPromoImageUrl,
  mobilePromoImageUrl,
  largeStarUrl,
  mediumStarUrl,
  smallStarUrl,
}) => {
  const desktopLargeStarRef = useRef();
  const desktopMediumStarRef = useRef();
  const desktopSmallStarRef = useRef();
  const mobileLargeStarRef = useRef();
  const mobileMediumStarRef = useRef();
  const mobileSmallStarRef = useRef();

  useScrollAnimation([
    { elementRef: desktopLargeStarRef, speed: 5 },
    { elementRef: desktopMediumStarRef, speed: 5, animationType: 'bounce' },
    { elementRef: desktopSmallStarRef, speed: 5 },

    { elementRef: mobileLargeStarRef, speed: 2 },
    { elementRef: mobileMediumStarRef, speed: 5, animationType: 'bounce' },
    { elementRef: mobileSmallStarRef, speed: 5 },
  ]);

  return (
    <Box className={styles.container}>
      <Box className={styles.brandLogo}>
        {brandLogoUrl && (
          <Box className={styles.logoWrapper}>
            {brandLogoLink ? (
              <Link href={brandLogoLink}>
                <img
                  className={styles.brandLogoImage}
                  src={brandLogoUrl}
                  alt="brand logo"
                />
              </Link>
            ) : (
              <img src={brandLogoUrl} alt="brand logo" />
            )}
          </Box>
        )}
        <Box className={styles.mobilePromoImages}>
          {largeStarUrl && (
            <img
              ref={mobileLargeStarRef}
              className={styles.mobileLargeStar}
              src={largeStarUrl}
              alt="large star logo"
            />
          )}
          {mediumStarUrl && (
            <img
              ref={mobileMediumStarRef}
              className={styles.mobileMediumStar}
              src={mediumStarUrl}
              alt="medium star logo"
            />
          )}
          {smallStarUrl && (
            <img
              ref={mobileSmallStarRef}
              className={styles.mobileSmallStar}
              src={smallStarUrl}
              alt="small star logo"
            />
          )}
          {mobilePromoImageUrl && (
            <img
              className={styles.mobilePromoImage}
              src={mobilePromoImageUrl}
              alt="mobile promo logo"
            />
          )}
        </Box>
      </Box>

      <Box className={styles.heroMain}>
        <Box className={styles.heroTextContainer}>
          <Box
            display="flex"
            flexDirection="column"
            className={classNames([nds('margin-vertical-3')])}
          >
            <Title className={styles.heroTitle}>
              {parseStyleFormatting(title)}
            </Title>

            <Box className={styles.heroSubtitle}>
              <Text component="p">{subtitle}</Text>
            </Box>
          </Box>
        </Box>

        <Box paddingTop="1" alignSelf="flex-end">
          <Box className={styles.desktopPromoContainer}>
            {largeStarUrl && (
              <img
                ref={desktopLargeStarRef}
                className={styles.desktopLargeStar}
                src={largeStarUrl}
                alt="large star logo"
              />
            )}
            {smallStarUrl && (
              <img
                ref={desktopSmallStarRef}
                className={styles.desktopSmallStar}
                src={smallStarUrl}
                alt="small star logo"
              />
            )}
            {mediumStarUrl && (
              <img
                ref={desktopMediumStarRef}
                className={styles.desktopMediumStar}
                src={mediumStarUrl}
                alt="medium star logo"
              />
            )}
            {desktopPromoImageUrl && (
              <img
                className={styles.desktopPromoImage}
                src={desktopPromoImageUrl}
                alt="desktop promo logo"
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

LayoutL2Insurance.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  brandLogoUrl: PropTypes.string,
  brandLogoLink: PropTypes.string,
  desktopPromoImageUrl: PropTypes.string,
  mobilePromoImageUrl: PropTypes.string,
  largeStarUrl: PropTypes.string,
  mediumStarUrl: PropTypes.string,
  smallStarUrl: PropTypes.string,
};

LayoutL2Insurance.defaultProps = {
  brandLogoUrl: '',
  brandLogoLink: '',
  desktopPromoImageUrl: '',
  mobilePromoImageUrl: '',
  largeStarUrl: '',
  mediumStarUrl: '',
  smallStarUrl: '',
};

LayoutL2Insurance.getEditingConfig = () => {
  return [
    stringTextAreaSpec('title', 'Title', {
      help: 'For Gotham black font surround with a | like |this|, For italics, surround title text with ^ like ^this^. To include a linebreak, insert :br: in the textbox',
    }),
    stringTextAreaSpec('subtitle', 'Subtitle'),
    stringInputSpec('brandLogoUrl', 'Brand Logo URL'),
    stringInputSpec('brandLogoLink', 'Brand Logo Link'),
    stringInputSpec('desktopPromoImageUrl', 'Desktop Promo Image URL'),
    stringInputSpec('mobilePromoImageUrl', 'Mobile Promo Image URL'),
    stringInputSpec('largeStarUrl', 'Large Star Image URL'),
    stringInputSpec('mediumStarUrl', 'Medium Star Image URL'),
    stringInputSpec('smallStarUrl', 'Small Star Image URL'),
  ];
};

export default LayoutL2Insurance;
